import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getMissionToStaffMission } from '../../api/mission-to-staffs/get-mission-to-staff-mission'
import { getStaff } from '../../api/staff/get-staff'
import useTranslate from '../../lib/hooks/use-translate'

type Props = {
  missionToStaffId: number
  version?: 'singleline' | 'multilines'
}

export function StaffMissionReportHeader({ missionToStaffId, version = 'singleline' }: Props) {
  const { t } = useTranslate()

  const { data: missionToStaff } = useQuery({
    queryKey: ['mission-to-staff', missionToStaffId],
    queryFn: () => {
      if (!missionToStaffId) throw new Error('No staff id')
      return getMissionToStaffMission(Number(missionToStaffId))
    },
    enabled: !!missionToStaffId
  })

  const { data } = useQuery({
    queryKey: ['staff', missionToStaff?.staffId],
    queryFn: () => {
      if (!missionToStaff?.staffId) throw new Error('No staff id')
      return getStaff(missionToStaff?.staffId, 'BASIC')
    },
    enabled: !!missionToStaff?.staffId
  })

  if (!data || !missionToStaff) return null

  return (
    <div
      className={cn('p-3 rounded-2xl flex items-center gap-4', {
        'bg-white-active': version === 'singleline',
        'bg-white border border-grey-light': version === 'multilines'
      })}
    >
      <img
        src={data.picture?.path ?? '/profile-default.jpg'}
        alt="photo du staff"
        className="w-20 h-20 rounded-xl object-cover"
      />
      <div className="flex-1">
        <Typography variant="text">{toFullName(data.firstName, data.lastName)}</Typography>
        <div className="h-2" />
        <div
          className={cn('flex items-center gap-2 p-2 rounded-sm', {
            'flex-col gap-1 items-start bg-white-hover': version === 'multilines',
            'flex-row bg-grey-light': version === 'singleline'
          })}
        >
          <Badge
            variant="fushia"
            text={`${t('staff.personal.space.missions.report.mission.date')} : ${DateFormatter.DDMMYYYY(missionToStaff.mission.date)}`}
          />{' '}
          {version === 'singleline' && '|'}
          <Typography variant="label-semibold">
            {t('staff.personal.space.missions.report.mission.title')} :{' '}
            {missionToStaff.mission.title}
          </Typography>
          {version === 'singleline' && '|'}{' '}
          <Typography variant="label-semibold">
            {t('staff.personal.space.missions.report.mission.meeting.place')} :{' '}
            {missionToStaff.mission.meetingPlace.label}
          </Typography>
        </div>
      </div>
    </div>
  )
}
